import React, { forwardRef, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './Subtabs.css';
import rawData from '../../../utils/tabsData.json';
import { TabsData } from '../../../types/navigation';
import { useNavigationTranslation } from '../../../hooks/useNavigationTranslation';

interface SubTabsProps {
  activeTab: string | null;
  isMobile: boolean;
  onNavigate: () => void;
}

const data = rawData as TabsData;

const Subtabs = forwardRef<HTMLDivElement, SubTabsProps>(
  ({ activeTab, isMobile, onNavigate }, ref) => {
    const [hoveredSubtab, setHoveredSubtab] = useState<string | null>(null);
    const activeTabData = data.tabs.find((tab) => tab.id === activeTab);
    const navigate = useNavigate();
    const { getSubTabLabel, getProductLabel } = useNavigationTranslation();

    useEffect(() => {
      if (activeTabData?.subtabs?.length && activeTabData.subtabs[0].id) {
        setHoveredSubtab(activeTabData.subtabs[0].id);
      }
    }, [activeTab, activeTabData]);

    const handleSubtabHover = (subtabId: string) => {
      if (!isMobile) {
        setHoveredSubtab(subtabId);
      }
    };

    if (!activeTabData || !activeTabData.subtabs?.length) return null;

    // Helper function to get image URL from the public folder
    const getImageSrc = (imageName: string) => {
      return `${process.env.PUBLIC_URL}/images/menu/${imageName}`;
    };

    const handleProductClick = (productId: string, subtabId: string) => {
      onNavigate(); // Close the menu before navigating
      const path = `/${subtabId}/${productId}`;
      navigate(path);
    };

    const hasSingleSubtab = activeTabData.subtabs.length === 1;
    const singleSubtab = activeTabData.subtabs[0];
    const visibleSubtabs = activeTabData.subtabs.filter(subtab => !subtab.isHidden);

    const renderProducts = (products: any[], subtabId: string) => {
      return (
        <ul className={"" + (products.length > 0 ? "products" : "no-products")}>
          {products
            .filter(product => !product.isHidden)
            .map((product) => (
              product.image ? (
                <li
                  key={product.id}
                  className="product-item"
                  onClick={() => handleProductClick(product.id, subtabId)}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="min-h-[250px]">
                    <img src={getImageSrc(product.image)} alt={getProductLabel(product.id)} />
                  </div>
                  <div className="product-label">{getProductLabel(product.id)}</div>
                </li>
              ) : (
                <div
                  className="product-design-box"
                  key={product.id}
                  onClick={() => handleProductClick(product.id, subtabId)}
                  style={{ cursor: 'pointer' }}
                >
                  {getProductLabel(product.id)}
                </div>
              )
            ))}
        </ul>
      );
    };

    return (
      <div className="subtabs-container min-h-[500px]" ref={ref}>
        {!hasSingleSubtab && (
          <ul className="subtabs">
            {visibleSubtabs.map((subtab) => (
              <li
                key={subtab.id}
                className="subtab-item"
                onMouseEnter={() => handleSubtabHover(subtab.id)}
              >
                <span className={`subtab-label ${hoveredSubtab === subtab.id ? 'active' : ''}`}>
                  {getSubTabLabel(subtab.id)}
                </span>
              </li>
            ))}
          </ul>
        )}
        {hasSingleSubtab && <div className="subtabs-spacer" />}

        <div className="subtab-contents">
          {hasSingleSubtab && singleSubtab?.products ? (
            <div className="subtab-content active">
              {renderProducts(singleSubtab.products, singleSubtab.id)}
            </div>
          ) : (
            visibleSubtabs.map((subtab) => (
              <div
                key={subtab.id}
                className={`subtab-content ${hoveredSubtab === subtab.id ? 'active' : ''}`}
              >
                {subtab.products && renderProducts(subtab.products, subtab.id)}
              </div>
            ))
          )}
        </div>
      </div>
    );
  }
);

Subtabs.displayName = 'Subtabs';

export default Subtabs;